import { getCurrentInstance, isRef, ref, Ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { DATA_STATE } from "@plugins/modal/modal.constant";
import contentSubpageEventBus from "@plugins/content-subpage/service/event-bus";
import { useDirty } from "@/plugins/common/composables/page-dirty";

export interface ContentSubpageParams {
  id?: string | Ref<string>;
  baseTitle?: string[];
  hasParentSubpage?: boolean;
  actionHidden?: () => void;
  actionBack?: () => void;
  overWriteTitle?: boolean;
  customBackLabel?: string;

  [key: string]: unknown;
}

export function useContentSubpage(params: ContentSubpageParams = {}, dirtyId?: Ref<string>) {
  let idRef;
  if (dirtyId) {
    idRef = dirtyId;
  } else {
    idRef = isRef(params.id) ? params.id : ref(params.id ?? "");
  }
  const pageDirty = useDirty(idRef);
  const backLabel = ref("");
  const { t } = useI18n();

  const instance = getCurrentInstance()!;

  const dataSaveButton = ref({
    kind: "primary" as const,
    size: "field" as const,
    error: false,
    loading: false,
    completed: false,
    iconOnly: true,
    tipPosition: "bottom" as const,
    tipAlignment: "end" as const,
    errorMessage: t("api_fetch_generic_error_message"),
    iconName: "Save24",
  });
  const defaultErrorMessage = t("api_action_generic_error_message");
  const visible = ref(true);
  const titles = ref<string[]>(params.baseTitle ? [...params.baseTitle] : []);
  const hasParentSubpage = ref<boolean>(!!params.hasParentSubpage);
  const ignoreActionHidden = ref(false);
  const ignoreActionBack = ref(false);

  function setVisible(value: boolean) {
    visible.value = value;
  }

  function actionHiddenCallBack() {
    if (!ignoreActionHidden.value && typeof params.actionHidden === "function") {
      params.actionHidden();
    }
  }

  function actionBackCallBack() {
    if (!ignoreActionBack.value && typeof params.actionBack === "function") {
      params.actionBack();
    }
  }

  function actionHidden() {
    contentSubpageEventBus.$emit("content-subpage-hidden", instance?.vnode.key);
  }

  function actionBack() {
    contentSubpageEventBus.$emit("content-subpage-back", instance?.vnode.key);
  }

  function getDataState() {
    if (dataSaveButton.value.loading) return DATA_STATE.LOADING;
    if (dataSaveButton.value.completed) return DATA_STATE.COMPLETED;
    if (dataSaveButton.value.error) return DATA_STATE.ERROR;
    return DATA_STATE.DEFAULT;
  }

  function setDataState(state: string) {
    resetDataState();
    switch (state) {
      case DATA_STATE.LOADING:
        dataSaveButton.value.loading = true;
        break;
      case DATA_STATE.LOADED:
        dataSaveButton.value.loading = false;
        break;
      case DATA_STATE.COMPLETED:
        dataSaveButton.value.loading = false;
        dataSaveButton.value.completed = true;
        break;
      case DATA_STATE.ERROR:
        dataSaveButton.value.loading = false;
        dataSaveButton.value.error = true;
        break;
    }
  }

  function setErrorMessage(message: string) {
    dataSaveButton.value.errorMessage = message;
  }

  function handleSaveButtonResetError() {
    dataSaveButton.value.loading = false;
    dataSaveButton.value.error = false;
    dataSaveButton.value.errorMessage = defaultErrorMessage;
  }

  function handleSaveButtonResetCompleted() {
    dataSaveButton.value.loading = false;
    dataSaveButton.value.completed = false;
  }

  function resetDataState() {
    dataSaveButton.value.completed = false;
    dataSaveButton.value.error = false;
    dataSaveButton.value.errorMessage = defaultErrorMessage;
  }

  watch(
    titles,
    () => {
      if (hasParentSubpage.value) {
        const final = params.customBackLabel ? params.customBackLabel : titles.value.at(-1);
        backLabel.value = `${t("content_subpage_go_back_link")} ${final}`;
      }
    },
    { immediate: true },
  );

  return {
    idRef,
    params,
    visible,
    dataSaveButton,
    titles,
    hasParentSubpage,
    ignoreActionHidden,
    ignoreActionBack,
    defaultErrorMessage,

    setVisible,
    actionHiddenCallBack,
    actionBackCallBack,
    actionHidden,
    actionBack,

    getDataState,
    setDataState,
    setErrorMessage,
    handleSaveButtonResetError,
    handleSaveButtonResetCompleted,
    resetDataState,
    ...pageDirty,
    backLabel,
    // minimal expose for content subpage, always expose this for all content subpage
    defaultExpose: {
      titles,
      params,
      setVisible,
      actionHiddenCallBack,
      actionBackCallBack,
      isComponentDirty: pageDirty.isComponentDirty,
      removeComponentDirty: pageDirty.removeComponentDirty,
      promptComponentDirty: pageDirty.promptComponentDirty,
    },
  };
}
